import { useState, useEffect } from 'react';
import {
    Settings,
    Moon,
    Sun,
    Bell,
    Mail,
    Shield,
    User,
    Lock,
    Globe,
    Smartphone,
    CheckCircle,
    AlertCircle,
    Save,
    X,
    Loader2,
    Palette,
    Monitor,
    Eye,
    EyeOff,
    Key,
    Fingerprint,
    LogOut,
    ChevronRight,
    RefreshCw
} from 'lucide-react';

export default function SettingsPage() {
    const [darkMode, setDarkMode] = useState(false);
    const [systemTheme, setSystemTheme] = useState(false);
    const [notifications, setNotifications] = useState(true);
    const [emailAlerts, setEmailAlerts] = useState(true);
    const [pushNotifications, setPushNotifications] = useState(true);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [autoSave, setAutoSave] = useState(true);
    const [compactMode, setCompactMode] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('preferences');

    // Initialize from localStorage on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        const savedSettings = localStorage.getItem('userSettings');

        if (savedTheme === 'dark') {
            setDarkMode(true);
            document.documentElement.classList.add('dark');
        } else {
            setDarkMode(false);
            document.documentElement.classList.remove('dark');
        }

        if (savedSettings) {
            try {
                const settings = JSON.parse(savedSettings);
                setNotifications(settings.notifications ?? true);
                setEmailAlerts(settings.emailAlerts ?? true);
                setPushNotifications(settings.pushNotifications ?? true);
                setSoundEnabled(settings.soundEnabled ?? true);
                setAutoSave(settings.autoSave ?? true);
                setCompactMode(settings.compactMode ?? false);
                setSystemTheme(settings.systemTheme ?? false);
            } catch (e) {
                console.error('Failed to load settings');
            }
        }
    }, []);

    const toggleDarkMode = () => {
        const newMode = !darkMode;
        setDarkMode(newMode);
        if (newMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    };

    const handleSave = async () => {
        setSaving(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));

        const settings = {
            notifications,
            emailAlerts,
            pushNotifications,
            soundEnabled,
            autoSave,
            compactMode,
            systemTheme,
            darkMode,
        };
        localStorage.setItem('userSettings', JSON.stringify(settings));

        setSaved(true);
        setSaving(false);
        setTimeout(() => setSaved(false), 3000);
    };

    const resetDefaults = () => {
        if (window.confirm('Reset all settings to default values?')) {
            setNotifications(true);
            setEmailAlerts(true);
            setPushNotifications(true);
            setSoundEnabled(true);
            setAutoSave(true);
            setCompactMode(false);
            setSystemTheme(false);
            if (!darkMode) toggleDarkMode();
        }
    };

    const tabs = [
        { id: 'preferences', label: 'Preferences', icon: Settings },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'appearance', label: 'Appearance', icon: Palette },
        { id: 'security', label: 'Security', icon: Shield },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-2xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Settings className="w-5 h-5 text-green-300" />
                                <span className="text-green-200 text-xs uppercase tracking-widest font-semibold">Settings</span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-extrabold">
                                Settings
                            </h1>
                            <p className="text-green-100/80 mt-2 max-w-2xl">
                                Customize your application preferences, notifications, and appearance.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={resetDefaults}
                                className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors text-sm flex items-center gap-2"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Reset Defaults
                            </button>
                        </div>
                    </div>
                </div>

                {/* Success Toast */}
                {saved && (
                    <div className="fixed bottom-6 right-6 z-50 animate-slideUp">
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="font-medium">Settings saved successfully!</span>
                            <button onClick={() => setSaved(false)} className="text-green-500 hover:text-green-700">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-1 flex flex-wrap gap-1">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive
                                        ? 'bg-[#3a663f] text-white shadow-lg'
                                        : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Settings Content */}
                <div className="space-y-6">
                    {/* Preferences Tab */}
                    {activeTab === 'preferences' && (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 md:p-8 space-y-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Settings className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                <h2 className="text-lg font-bold text-gray-800 dark:text-white">General Preferences</h2>
                            </div>

                            <ToggleRow
                                icon={AutoSaveIcon}
                                label="Auto-Save"
                                description="Automatically save your settings changes"
                                enabled={autoSave}
                                onToggle={() => setAutoSave(!autoSave)}
                            />

                            <ToggleRow
                                icon={CompactIcon}
                                label="Compact Mode"
                                description="Reduce spacing and make the UI more compact"
                                enabled={compactMode}
                                onToggle={() => setCompactMode(!compactMode)}
                            />

                            <ToggleRow
                                icon={SystemIcon}
                                label="Use System Theme"
                                description="Automatically match your system's theme"
                                enabled={systemTheme}
                                onToggle={() => setSystemTheme(!systemTheme)}
                            />
                        </div>
                    )}

                    {/* Notifications Tab */}
                    {activeTab === 'notifications' && (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 md:p-8 space-y-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Bell className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                <h2 className="text-lg font-bold text-gray-800 dark:text-white">Notification Preferences</h2>
                            </div>

                            <ToggleRow
                                icon={BellIcon}
                                label="Push Notifications"
                                description="Receive real-time alerts in the portal"
                                enabled={notifications}
                                onToggle={() => setNotifications(!notifications)}
                            />

                            <ToggleRow
                                icon={MailIcon}
                                label="Email Alerts"
                                description="Get notified via email for important updates"
                                enabled={emailAlerts}
                                onToggle={() => setEmailAlerts(!emailAlerts)}
                            />

                            <ToggleRow
                                icon={DeviceIcon}
                                label="Mobile Push Notifications"
                                description="Receive notifications on your mobile device"
                                enabled={pushNotifications}
                                onToggle={() => setPushNotifications(!pushNotifications)}
                            />

                            <ToggleRow
                                icon={SoundIcon}
                                label="Sound Alerts"
                                description="Play a sound when notifications arrive"
                                enabled={soundEnabled}
                                onToggle={() => setSoundEnabled(!soundEnabled)}
                            />
                        </div>
                    )}

                    {/* Appearance Tab */}
                    {activeTab === 'appearance' && (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 md:p-8 space-y-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Palette className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                <h2 className="text-lg font-bold text-gray-800 dark:text-white">Appearance Settings</h2>
                            </div>

                            {/* Dark Mode Toggle */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-slate-600 flex items-center justify-center">
                                        {darkMode ? (
                                            <Moon className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                                        ) : (
                                            <Sun className="w-6 h-6 text-yellow-500" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-800 dark:text-white">Dark Mode</p>
                                        <p className="text-xs text-gray-500 dark:text-slate-400">
                                            {darkMode ? 'Dark mode is currently active' : 'Switch between light and dark themes'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={toggleDarkMode}
                                    className={`relative w-14 h-8 rounded-full transition-colors duration-300 ${darkMode ? 'bg-[#3a663f]' : 'bg-gray-300 dark:bg-slate-600'
                                        }`}
                                    aria-label="Toggle dark mode"
                                >
                                    <span
                                        className={`absolute top-0.5 w-7 h-7 rounded-full bg-white shadow-md transition-all duration-300 ${darkMode ? 'left-6.5' : 'left-0.5'
                                            }`}
                                    />
                                </button>
                            </div>

                            {/* Theme Preview */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                                <ThemeOption
                                    color="green"
                                    label="Mayfair Green"
                                    active={true}
                                />
                                <ThemeOption
                                    color="blue"
                                    label="Ocean Blue"
                                    active={false}
                                />
                                <ThemeOption
                                    color="purple"
                                    label="Royal Purple"
                                    active={false}
                                />
                            </div>
                        </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 md:p-8 space-y-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Shield className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                <h2 className="text-lg font-bold text-gray-800 dark:text-white">Security Settings</h2>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                            <Key className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800 dark:text-white">Change Password</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400">Update your account password</p>
                                        </div>
                                    </div>
                                    <button className="px-4 py-2 text-sm text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                                        Update
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                            <Fingerprint className="w-6 h-6 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800 dark:text-white">Two-Factor Authentication</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400">Add an extra layer of security</p>
                                        </div>
                                    </div>
                                    <button className="px-4 py-2 text-sm text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                                        Setup
                                    </button>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                            <LogOut className="w-6 h-6 text-red-600 dark:text-red-400" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800 dark:text-white">Active Sessions</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400">Manage your active sessions</p>
                                        </div>
                                    </div>
                                    <button className="px-4 py-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                        View All
                                    </button>
                                </div>
                            </div>

                            {/* Session Info */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                                <div className="flex items-start gap-3">
                                    <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Current Session</p>
                                        <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                                            You are logged in from {navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'} device
                                            <br />
                                            IP: {window.location.hostname} � Last active: {new Date().toLocaleTimeString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Save Button */}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-4 rounded-xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl transition-all duration-300 transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {saving ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Save className="w-5 h-5" />
                            Save All Settings
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function ToggleRow({ icon: Icon, label, description, enabled, onToggle }) {
    return (
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl transition-colors hover:bg-gray-100 dark:hover:bg-slate-700/70">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white dark:bg-slate-600 flex items-center justify-center text-gray-500 dark:text-slate-300 shadow-sm">
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <p className="font-medium text-gray-800 dark:text-white">{label}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">{description}</p>
                </div>
            </div>
            <button
                onClick={onToggle}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#3a663f]/50 ${enabled ? 'bg-[#3a663f]' : 'bg-gray-300 dark:bg-slate-600'
                    }`}
                aria-label={`Toggle ${label}`}
            >
                <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${enabled ? 'left-6.5' : 'left-0.5'
                        }`}
                />
            </button>
        </div>
    );
}

function ThemeOption({ color, label, active }) {
    const colorMap = {
        green: 'bg-[#3a663f]',
        blue: 'bg-blue-600',
        purple: 'bg-purple-600',
    };

    return (
        <div className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${active
                ? 'border-[#3a663f] dark:border-green-400 bg-[#3a663f]/5 dark:bg-[#3a663f]/10'
                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
            }`}>
            <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full ${colorMap[color]} shadow-md`} />
                <div>
                    <p className="font-medium text-gray-800 dark:text-white">{label}</p>
                    {active && (
                        <span className="text-xs text-[#3a663f] dark:text-green-400 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Active
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

// ========== ICON COMPONENTS ==========

function AutoSaveIcon({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
        </svg>
    );
}

function CompactIcon({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
    );
}

function SystemIcon({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    );
}

function BellIcon({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
    );
}

function MailIcon({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    );
}

function DeviceIcon({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
    );
}

function SoundIcon({ className }) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
        </svg>
    );
}